import React, { useEffect, useRef, useState } from 'react';
import {
    MedicalOpinion,
    MedicalOpinionCloseReason,
    MedicalOpinionFile,
} from '../../types/api';
import { ModalWrapper } from './ModalWrapper';
import { CloseMedicalOpinionModal } from './CloseMedicalOpinionModal';
import { CallPatientConfModal } from './CallPatientConfModal';
import { MedicalOpinionFilePreview } from '../medicalOpinion/MedicalOpinionFilePreview';
import { MedicalOpinionFileRow } from '../medicalOpinion/MedicalOpinionFileRow';
import { base64ToObjectUrl, downloadBase64File } from '../medicalOpinion/medicalOpinionFiles';
import { MODAL_WIDTHS, POPUP_STYLES } from '../../constants/popupStyles';
import { useMedicalOpinionTypes } from '../../hooks/useMedicalOpinionOptions';
import apiService from '../../services/api';
import { getTextDirection } from '../../utils/textDirection';
import { displayPhoneNumber } from '../../utils/phoneUtils';

interface MedicalOpinionObligationApprovalModalProps {
    isOpen: boolean;
    onClose: () => void;
    token: string;
    opinion: MedicalOpinion | null;
    patientName: string;
    patientTz: string;
    patientHmId: string;
    patientPhone: string;
    // Offered alongside the patient's own number in the call modal, when the caller has
    // them. Absent from a task row, present on the patient page.
    parent1Phone?: string;
    parent2Phone?: string;
    clinicName: string;
    // The obligation decision is admin-only on the backend. A non-admin gets the same modal
    // as a read-only view, with no actions and no "Approval" in the caption.
    isAdmin?: boolean;
    // Resolves a user id to a display name, same prop shape as CaseClosureApprovalModal.
    // Used for both the assigned doctor and the requester, which come off the opinion record
    // rather than being passed in - the patient's doctor_id is not necessarily the one
    // assigned to this request.
    getUserName?: (userId: number) => string;
    // Called after a successful approve or close, so the caller can refresh its list.
    onSuccess: () => void;
}

const formatDateTime = (timestamp?: number): string => {
    if (!timestamp) return '—';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '—';
    return `${date.toLocaleDateString('en-GB')} ${date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
    })}`;
};

export const MedicalOpinionObligationApprovalModal: React.FC<MedicalOpinionObligationApprovalModalProps> = ({
    isOpen,
    onClose,
    token,
    opinion,
    patientName,
    patientTz,
    patientHmId,
    patientPhone,
    parent1Phone,
    parent2Phone,
    clinicName,
    isAdmin = false,
    getUserName,
    onSuccess,
}) => {
    // The same call flow as the patient page's phone icon: a small modal offering Phone or
    // WhatsApp over the numbers on record, rather than this icon deciding for the user.
    const [isCallModalOpen, setIsCallModalOpen] = useState(false);
    const [callMessage, setCallMessage] = useState<string | null>(null);

    // Only the type list is needed here: the status and close reason labels arrive resolved
    // on the record, and the close reason list itself belongs to the child close modal.
    const { labels: opinionTypeLabels } = useMedicalOpinionTypes();

    const [obligation, setObligation] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState<string | null>(null);

    const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewType, setPreviewType] = useState<string | null>(null);
    const [previewName, setPreviewName] = useState<string | null>(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

    const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
    const [closeError, setCloseError] = useState<string | null>(null);

    // Revoked whenever the preview is replaced and on unmount, so selecting several files
    // in a row does not leak a Blob per click.
    const previewUrlRef = useRef<string | null>(null);
    const setPreview = (url: string | null) => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
        }
        previewUrlRef.current = url;
        setPreviewUrl(url);
    };

    // Seed the editable obligation from the record each time a different request is opened.
    useEffect(() => {
        if (isOpen && opinion) {
            setObligation(opinion.obligation ? String(opinion.obligation) : '');
            setError(null);
            setSelectedFileId(null);
            setPreview(null);
            setPreviewType(null);
            setPreviewName(null);
            setPreviewError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, opinion?.id]);

    useEffect(() => {
        return () => {
            if (previewUrlRef.current) {
                URL.revokeObjectURL(previewUrlRef.current);
            }
        };
    }, []);

    if (!isOpen || !opinion) return null;

    // The backend rejects obligation_response from a non-admin (403) and for any status other
    // than pending_obligation, so the actions are hidden rather than offered and failed. With
    // no actions on offer the modal is not an approval screen, so the caption says so.
    const canAct = isAdmin && opinion.status === 'pending_obligation';
    const title = canAct ? 'Medical Opinion Obligation Approval' : 'Medical Opinion Obligation';

    // Names come resolved from the backend, because the caller's user list is filtered by
    // institution and often does not contain the assigned doctor at all. getUserName is only
    // a fallback for a response predating those fields.
    // created_by is 0 for a request the patient opened in the app - there is no user row 0.
    const requestedByName =
        opinion.created_by === 0
            ? 'Patient (app)'
            : opinion.created_by_name
                || getUserName?.(opinion.created_by)
                || `User #${opinion.created_by}`;

    // From the opinion, not the patient: doctor_id is copied off the patient at creation so
    // the two normally agree, but this request's assigned doctor is what matters here - and
    // the patient's doctor_id can be 0 while the opinion's is set.
    const doctorName = opinion.doctor_id
        ? opinion.doctor_name || getUserName?.(opinion.doctor_id) || `User #${opinion.doctor_id}`
        : '';

    const copy = async (label: string, value: string) => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            setCopied(label);
            setTimeout(() => setCopied((c) => (c === label ? null : c)), 1500);
        } catch {
            setError('Could not copy to clipboard');
        }
    };

    // Clicking the selected file again closes the preview pane.
    const handleSelectFile = async (file: MedicalOpinionFile) => {
        if (selectedFileId === file.file_id) {
            setSelectedFileId(null);
            setPreview(null);
            setPreviewType(null);
            setPreviewName(null);
            setPreviewError(null);
            return;
        }

        setSelectedFileId(file.file_id);
        setPreviewName(file.filename);
        setPreviewType(file.content_type);
        setPreviewError(null);
        setIsPreviewLoading(true);
        setPreview(null);
        try {
            const result = await apiService.getMedicalOpinionFile({
                token,
                patient_id: opinion.patient_id,
                medical_opinion_id: opinion.id,
                file_id: file.file_id,
            });
            if (result?.status === 'error' || !result?.base64file) {
                setPreviewError(result?.message || 'File could not be loaded');
                return;
            }
            setPreview(base64ToObjectUrl(result.base64file, result.content_type || file.content_type));
            setPreviewType(result.content_type || file.content_type);
        } catch (err: any) {
            setPreviewError(
                err?.response?.status === 403
                    ? 'You are not authorized to open this file'
                    : 'File could not be loaded'
            );
        } finally {
            setIsPreviewLoading(false);
        }
    };

    const handleDownloadFile = async (file: MedicalOpinionFile) => {
        setDownloadingFileId(file.file_id);
        setError(null);
        try {
            const result = await apiService.getMedicalOpinionFile({
                token,
                patient_id: opinion.patient_id,
                medical_opinion_id: opinion.id,
                file_id: file.file_id,
            });
            if (result?.status === 'error' || !result?.base64file) {
                setError(result?.message || 'File could not be downloaded');
                return;
            }
            downloadBase64File(
                result.base64file,
                result.content_type || file.content_type,
                result.filename || file.filename
            );
        } catch (err: any) {
            setError(
                err?.response?.status === 403
                    ? 'You are not authorized to open this file'
                    : 'File could not be downloaded'
            );
        } finally {
            setDownloadingFileId(null);
        }
    };

    const validateObligation = (): number | null => {
        const trimmed = obligation.trim();
        if (!trimmed) {
            setError('Please enter the obligation number');
            return null;
        }
        if (!/^\d+$/.test(trimmed)) {
            setError('Obligation must be a number');
            return null;
        }
        return Number(trimmed);
    };

    const handleApprove = async () => {
        const value = validateObligation();
        if (value === null) return;

        setIsSaving(true);
        setError(null);
        try {
            const result = await apiService.medicalOpinionObligationResponse({
                token,
                patient_id: opinion.patient_id,
                medical_opinion_id: opinion.id,
                obligation: value,
                is_approved: true,
            });
            if (result?.status === 'error') {
                setError(result.message || 'Failed to approve the obligation');
                return;
            }
            onSuccess();
            onClose();
        } catch (err: any) {
            setError(
                err?.response?.status === 403
                    ? 'You are not authorized to handle this obligation'
                    : err?.message || 'Failed to approve the obligation'
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleConfirmClose = async (closeReason: MedicalOpinionCloseReason, notes: string) => {
        // The obligation is still sent on the closing path - the design patches it either
        // way, so a correction the user made is not silently dropped.
        const value = validateObligation();
        if (value === null) {
            setIsCloseModalOpen(false);
            return;
        }

        setIsSaving(true);
        setCloseError(null);
        try {
            const result = await apiService.medicalOpinionObligationResponse({
                token,
                patient_id: opinion.patient_id,
                medical_opinion_id: opinion.id,
                obligation: value,
                is_approved: false,
                close_reason: closeReason,
                close_reason_notes: notes,
            });
            if (result?.status === 'error') {
                setCloseError(result.message || 'Failed to close the request');
                return;
            }
            setIsCloseModalOpen(false);
            onSuccess();
            onClose();
        } catch (err: any) {
            setCloseError(
                err?.response?.status === 403
                    ? 'You are not authorized to handle this obligation'
                    : err?.message || 'Failed to close the request'
            );
        } finally {
            setIsSaving(false);
        }
    };

    // Sized and coloured to sit with the bold captions next to them, not to recede from them.
    const copyButton = (label: string, value: string) => (
        <button
            data-ai-id="opinion-obligation-copy-field"
            onClick={() => copy(label, value)}
            disabled={!value}
            className="text-[#366A75] hover:text-[#2D5A60] disabled:opacity-40 align-middle"
            title={copied === label ? 'Copied' : `Copy ${label}`}
        >
            {copied === label ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
            ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
            )}
        </button>
    );

    // Input files only. The list route returns every file on the request, but the letter the
    // psychiatrist produces (output_summary / output_other) belongs to the Opinion tab of the
    // Psychiatric Opinion Letter modal - the secretary handling the obligation should see
    // what the patient supplied, not the finished letter.
    const files = (opinion.files || []).filter((f) => f.file_type === 'input');
    const isPreviewOpen = selectedFileId !== null;

    return (
        <>
            <ModalWrapper modalId="MedicalOpinionObligationApprovalModal"
                isOpen={isOpen}
                onClose={onClose}
                title={title}
                isLoading={isSaving}
                // Grows sideways rather than resizing the form: max-w-2xl is 42rem, less the
                // 2rem of px-8 either side, so the left column is 38rem either way.
                // Open width = 38 + gap 1.5 + 38 + 4 padding = 81.5rem.
                width={isPreviewOpen ? 'max-w-[82rem]' : MODAL_WIDTHS.xxl}
            >
                <div className={isPreviewOpen ? 'flex gap-6 items-stretch' : ''}>
                <div className={isPreviewOpen ? 'w-[38rem] shrink-0 flex flex-col' : ''}>
                <div className="space-y-4 mb-6">
                    {/* Patient details on the left, request details on the right. Same block
                        style as Case Closure Approval. */}
                    <div className="flex gap-x-6 text-base text-[#366A75]">
                        {/* Left column - patient */}
                        <div className="flex flex-col gap-y-1 flex-1">
                            <div><span className="font-bold">Name:</span> {patientName || '—'}</div>
                            <div className="flex items-center gap-1">
                                <span><span className="font-bold">Id (TZ):</span> {patientTz || '—'}</span>
                                {copyButton('Id (TZ)', patientTz)}
                            </div>
                            <div className="flex items-center gap-1">
                                <span><span className="font-bold">HM_ID:</span> {patientHmId || '—'}</span>
                                {copyButton('HM_ID', patientHmId)}
                            </div>
                            <div className="flex items-center gap-1">
                                <span>
                                    <span className="font-bold">Phone:</span>{' '}
                                    {displayPhoneNumber(patientPhone) || '—'}
                                </span>
                                <button
                                    onClick={() => setIsCallModalOpen(true)}
                                    // Any number on record is enough: a youth patient often
                                    // has none of their own and both parents' in the picker.
                                    disabled={!patientPhone && !parent1Phone && !parent2Phone}
                                    className="text-[#366A75] hover:text-[#2D5A60] disabled:opacity-40 align-middle"
                                    title="Call patient"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                </button>
                            </div>
                            <div><span className="font-bold">Clinic:</span> {clinicName || '—'}</div>
                            <div><span className="font-bold">Doctor:</span> {doctorName || '—'}</div>
                        </div>
                        {/* Right column - request */}
                        <div className="flex flex-col gap-y-1 flex-1">
                            <div>
                                <span className="font-bold">Opinion Type:</span>{' '}
                                {opinionTypeLabels[opinion.opinion_type] || opinion.opinion_type}
                            </div>
                            <div><span className="font-bold">Requested By:</span> {requestedByName}</div>
                            <div><span className="font-bold">Requested At:</span> {formatDateTime(opinion.created_at)}</div>
                            <div>
                                <span className="font-bold">Status:</span>{' '}
                                {opinion.heb_status || opinion.status}
                            </div>
                            {/* Only meaningful once the request has been closed. */}
                            {opinion.status === 'closed' && (
                                <>
                                    <div>
                                        <span className="font-bold">Close Reason:</span>{' '}
                                        {opinion.heb_close_reason || opinion.close_reason || '—'}
                                    </div>
                                    {/* Free text written by a secretary, so it can be in either
                                        language - the direction follows the content. The caption
                                        stays LTR, only the value flips. */}
                                    <div>
                                        <span className="font-bold">Close Notes:</span>{' '}
                                        <span
                                            dir={getTextDirection(opinion.close_reason_notes)}
                                            className="inline-block align-top"
                                        >
                                            {opinion.close_reason_notes || '—'}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Request Details
                        </label>
                        <textarea
                            value={opinion.initiator_notes || ''}
                            rows={2}
                            readOnly
                            dir={getTextDirection(opinion.initiator_notes)}
                            className={`${POPUP_STYLES.inputBordered} resize-none opacity-80`}
                        />
                    </div>

                    {/* Obligation - the only editable field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Obligation Number {canAct && <span className="text-red-500">*</span>}
                        </label>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                inputMode="numeric"
                                value={obligation}
                                onChange={(e) => setObligation(e.target.value)}
                                readOnly={!canAct}
                                className={`${POPUP_STYLES.inputBordered} ${canAct ? '' : 'opacity-80'}`}
                                disabled={isSaving}
                            />
                            {copyButton('Obligation', obligation)}
                        </div>
                    </div>

                    {/* Files - the preview itself renders in the right pane */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Attached Files
                        </label>
                        {files.length === 0 ? (
                            <p className="text-sm text-gray-500">No files attached to this request</p>
                        ) : (
                            <div>
                                <ul className="space-y-1">
                                    {files.map((file) => (
                                        <MedicalOpinionFileRow
                                            key={file.id}
                                            filename={file.filename}
                                            isSelected={selectedFileId === file.file_id}
                                            onPreview={() => handleSelectFile(file)}
                                            onDownload={() => handleDownloadFile(file)}
                                            isDownloading={downloadingFileId === file.file_id}
                                        />
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-100 border-[1px] border-red-400 text-red-700 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                {/* Approve Obligation | Cancel | Close Request, left to right */}
                <div className="flex justify-center gap-4">
                    {canAct && (
                        <button onClick={handleApprove} disabled={isSaving} className={POPUP_STYLES.button}>
                            {isSaving ? 'Approving...' : 'Approve Obligation'}
                        </button>
                    )}
                    <button onClick={onClose} disabled={isSaving} className={POPUP_STYLES.button}>
                        Cancel
                    </button>
                    {canAct && (
                        <button
                            onClick={() => {
                                setCloseError(null);
                                setIsCloseModalOpen(true);
                            }}
                            disabled={isSaving}
                            className={POPUP_STYLES.button}
                        >
                            Close Request
                        </button>
                    )}
                </div>
                </div>

                {/* Right side - file preview. Rendered only while a file is selected; clicking
                    the same file again closes it, which is why there is no header or close
                    control here. items-stretch on the row makes this match the form's height,
                    so the pane runs from the patient details down to the buttons. */}
                {isPreviewOpen && (
                    <div className="w-[38rem] shrink-0 flex flex-col border-l border-gray-200 pl-6">
                        <MedicalOpinionFilePreview
                            src={previewUrl}
                            contentType={previewType}
                            filename={previewName}
                            isLoading={isPreviewLoading}
                            error={previewError}
                            heightClass="flex-1 min-h-0"
                        />
                    </div>
                )}
                </div>
            </ModalWrapper>

            {callMessage && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1100] px-6 py-3 rounded-lg shadow-lg bg-white text-sm text-[#366A75]">
                    {callMessage}
                </div>
            )}

            {isCallModalOpen && (
                <CallPatientConfModal
                    patientId={opinion.patient_id}
                    patientName={patientName}
                    patientPhone={patientPhone}
                    parent1Phone={parent1Phone}
                    parent2Phone={parent2Phone}
                    token={token}
                    onClose={() => setIsCallModalOpen(false)}
                    onSuccess={() => {
                        setCallMessage('Call initiated successfully!');
                        setTimeout(() => setCallMessage(null), 3000);
                    }}
                    onError={(msg) => {
                        setCallMessage(msg);
                        setTimeout(() => setCallMessage(null), 3000);
                    }}
                />
            )}

            <CloseMedicalOpinionModal
                isOpen={isCloseModalOpen}
                onClose={() => {
                    setIsCloseModalOpen(false);
                    setCloseError(null);
                }}
                onConfirm={handleConfirmClose}
                isSaving={isSaving}
                serverError={closeError}
            />
        </>
    );
};
